import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Filter, Loader2, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { PORTFOLIO_NONE } from '@/utils/entityCrossFilter';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import type { Portfolio } from '@/types/portfolios';

const NO_PORTFOLIO_OPTION: Portfolio = {
  portfolioId: PORTFOLIO_NONE,
  name: 'No portfolio',
  state: '—',
};

function parsePortfolios(data: unknown): Portfolio[] {
  if (!data || typeof data !== 'object') return [];
  const root = data as Record<string, unknown>;
  const list = Array.isArray(root.portfolios) ? root.portfolios : [];
  return list.filter((item): item is Portfolio => !!item && typeof item === 'object');
}

function normalizePortfolioId(portfolioId: string): string {
  return portfolioId.trim();
}

function portfolioFilterLabel(selectedIds: string[], options: Portfolio[]): string {
  if (selectedIds.length === 0) return 'All portfolios';
  if (selectedIds.length === 1) {
    const match = options.find(
      (p) => normalizePortfolioId(p.portfolioId) === normalizePortfolioId(selectedIds[0] ?? '')
    );
    return match?.name || selectedIds[0] || '1 portfolio';
  }
  return `${selectedIds.length} portfolios selected`;
}

function togglePortfolioId(selectedIds: string[], portfolioId: string, checked: boolean): string[] {
  const normalized = normalizePortfolioId(portfolioId);
  if (checked) {
    if (selectedIds.some((id) => normalizePortfolioId(id) === normalized)) return selectedIds;
    return [...selectedIds, portfolioId];
  }
  return selectedIds.filter((id) => normalizePortfolioId(id) !== normalized);
}

function resolvePortfolioName(portfolioId: string, options: Portfolio[]): string {
  const match = options.find(
    (p) => normalizePortfolioId(p.portfolioId) === normalizePortfolioId(portfolioId)
  );
  return match?.name || portfolioId;
}

function filterPortfoliosBySearch(options: Portfolio[], raw: string, limit = 100): Portfolio[] {
  const query = raw.trim().toLowerCase();
  const matched = query
    ? options.filter((portfolio) => {
        const haystack = `${portfolio.name || ''} ${portfolio.portfolioId}`.toLowerCase();
        return haystack.includes(query);
      })
    : options;
  return matched.slice(0, limit);
}

export function DaypartingPortfolioFilter({
  profileId,
  selectedPortfolioIds,
  onChange,
  replacesFilterLabel = null,
}: {
  profileId: string;
  selectedPortfolioIds: string[];
  onChange: (portfolioIds: string[]) => void;
  replacesFilterLabel?: string | null;
}) {
  const labelId = useId();
  const searchInputId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draftPortfolioIds, setDraftPortfolioIds] = useState<string[]>([]);
  const [confirmReplace, setConfirmReplace] = useState(false);

  const portfoliosQuery = useQuery({
    queryKey: ['dayparting-portfolio-options', profileId],
    queryFn: async () => {
      const res = await adsApi.listPortfolios(profileId);
      if (!res.success) throw new Error('Failed to load portfolios');
      return parsePortfolios(res.data).filter(
        (portfolio) => String(portfolio.state || '').toUpperCase() !== 'ARCHIVED'
      );
    },
    enabled: Boolean(profileId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const options = useMemo(() => {
    const portfolios = [...(portfoliosQuery.data ?? [])].sort((a, b) =>
      (a.name || a.portfolioId).localeCompare(b.name || b.portfolioId, undefined, {
        sensitivity: 'base',
      })
    );
    return [NO_PORTFOLIO_OPTION, ...portfolios];
  }, [portfoliosQuery.data]);

  const visibleOptions = useMemo(
    () => filterPortfoliosBySearch(options, search, 100),
    [options, search]
  );

  const activePortfolioIds = open ? draftPortfolioIds : selectedPortfolioIds;
  const selectedSet = useMemo(
    () => new Set(activePortfolioIds.map((id) => normalizePortfolioId(id))),
    [activePortfolioIds]
  );

  const triggerLabel = useMemo(
    () => portfolioFilterLabel(selectedPortfolioIds, options),
    [selectedPortfolioIds, options]
  );

  const loading = portfoliosQuery.isPending || portfoliosQuery.isFetching;
  const isFiltered = selectedPortfolioIds.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setConfirmReplace(false);
    if (nextOpen) {
      setDraftPortfolioIds(selectedPortfolioIds);
      return;
    }
    setDraftPortfolioIds(selectedPortfolioIds);
    setSearch('');
  };

  const updateDraftPortfolioIds = (next: string[]) => {
    setDraftPortfolioIds(next);
    if (next.length === 0) setConfirmReplace(false);
  };

  const selectVisible = () => {
    const next = new Set(draftPortfolioIds.map((id) => normalizePortfolioId(id)));
    const merged = [...draftPortfolioIds];
    for (const portfolio of visibleOptions) {
      const normalized = normalizePortfolioId(portfolio.portfolioId);
      if (next.has(normalized)) continue;
      next.add(normalized);
      merged.push(portfolio.portfolioId);
    }
    updateDraftPortfolioIds(merged);
  };

  const applyDraft = () => {
    onChange(draftPortfolioIds);
    setOpen(false);
    setSearch('');
    setConfirmReplace(false);
  };

  const handleDone = () => {
    if (replacesFilterLabel && draftPortfolioIds.length > 0) {
      setConfirmReplace(true);
      return;
    }
    applyDraft();
  };

  return (
    <div
      className="rounded-xl border border-border bg-surface p-3 shadow-sm sm:p-4"
      role="group"
      aria-labelledby={labelId}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <p id={labelId} className="text-sm font-semibold text-foreground">
            Portfolio filter
          </p>
          <p className="text-xs text-foreground-subtle">
            Limit the heatmap and summary cards to campaigns in selected portfolios.
            Leave unselected to include all campaigns.
            {replacesFilterLabel
              ? ' Applying a portfolio filter will replace the current campaign filter.'
              : ''}
          </p>
        </div>

        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={!profileId}
              aria-labelledby={labelId}
              aria-haspopup="listbox"
              aria-expanded={open}
              className={cn(
                'inline-flex w-full min-w-[14rem] max-w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm lg:w-auto lg:min-w-[18rem]',
                'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                isFiltered
                  ? 'border-brand/40 bg-brand-subtle/40 text-foreground hover:bg-brand-subtle/60'
                  : 'border-border bg-page text-foreground hover:bg-page/80'
              )}
            >
              <span className="inline-flex min-w-0 items-center gap-2">
                <Filter
                  className={cn(
                    'h-4 w-4 shrink-0',
                    isFiltered ? 'text-brand-deep' : 'text-foreground-muted'
                  )}
                  aria-hidden
                />
                <span className="truncate text-left">{triggerLabel}</span>
              </span>
              {loading ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[min(26rem,calc(100vw-2rem))] p-0">
            <div className="space-y-3 border-b border-border px-3 py-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-foreground">Select portfolios</p>
                  <p className="text-xs text-foreground-subtle">
                    Search by name or ID. Check one or more portfolios.
                  </p>
                </div>
                {isFiltered ? (
                  <button
                    type="button"
                    className="shrink-0 text-xs font-medium text-foreground-muted hover:text-foreground"
                    onClick={() => updateDraftPortfolioIds([])}
                  >
                    Clear all
                  </button>
                ) : null}
              </div>
              <div>
                <label htmlFor={searchInputId} className="sr-only">
                  Search portfolios
                </label>
                <input
                  id={searchInputId}
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or ID"
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                  autoFocus
                />
              </div>
              {visibleOptions.length > 0 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-brand-deep hover:underline"
                  onClick={selectVisible}
                >
                  Select all matching ({visibleOptions.length})
                </button>
              ) : null}
            </div>

            <div className="max-h-64 overflow-y-auto px-1 py-1" role="listbox" aria-multiselectable="true">
              {portfoliosQuery.isError ? (
                <p className="px-3 py-4 text-sm text-error">Failed to load portfolios.</p>
              ) : loading && options.length <= 1 ? (
                <p className="px-3 py-4 text-sm text-foreground-muted">Loading portfolios…</p>
              ) : visibleOptions.length === 0 ? (
                <p className="px-3 py-4 text-sm text-foreground-muted">No portfolios match.</p>
              ) : (
                <ul className="m-0 list-none p-0">
                  {visibleOptions.map((portfolio) => {
                    const checked = selectedSet.has(normalizePortfolioId(portfolio.portfolioId));
                    return (
                      <li key={portfolio.portfolioId} role="option" aria-selected={checked}>
                        <label
                          className={cn(
                            'flex cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2.5 text-sm',
                            checked ? 'bg-brand-subtle/50' : 'hover:bg-surface-muted'
                          )}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand/30"
                            checked={checked}
                            onChange={(e) =>
                              updateDraftPortfolioIds(
                                togglePortfolioId(
                                  draftPortfolioIds,
                                  portfolio.portfolioId,
                                  e.target.checked
                                )
                              )
                            }
                          />
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-foreground">
                              {portfolio.name || portfolio.portfolioId}
                            </span>
                            <span className="block truncate text-xs text-foreground-muted">
                              {portfolio.portfolioId === PORTFOLIO_NONE
                                ? 'Campaigns with no portfolio'
                                : portfolio.portfolioId}
                              {portfolio.state && portfolio.portfolioId !== PORTFOLIO_NONE
                                ? ` · ${portfolio.state}`
                                : ''}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="space-y-2 border-t border-border px-3 py-2.5">
              {confirmReplace ? (
                <>
                  <p className="text-xs text-foreground-subtle">
                    Apply these portfolios? This will replace the current campaign filter ({replacesFilterLabel}).
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setConfirmReplace(false)}>
                      Back
                    </Button>
                    <Button size="sm" onClick={applyDraft}>
                      Replace and apply
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-foreground-subtle">
                    {draftPortfolioIds.length > 0
                      ? `${draftPortfolioIds.length} portfolio${draftPortfolioIds.length === 1 ? '' : 's'} selected`
                      : 'All portfolios included'}
                    {replacesFilterLabel && draftPortfolioIds.length > 0
                      ? ' · This will replace the current campaign filter.'
                      : ''}
                  </p>
                  <Button variant="secondary" size="sm" onClick={handleDone}>
                    Done
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {isFiltered ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/70 pt-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">
            Active
          </span>
          {selectedPortfolioIds.map((portfolioId) => (
            <span
              key={portfolioId}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-brand/30 bg-brand-subtle/60 px-2.5 py-1 text-xs text-foreground"
            >
              <span className="truncate">{resolvePortfolioName(portfolioId, options)}</span>
              <button
                type="button"
                className="shrink-0 rounded-full text-foreground-muted hover:text-foreground"
                aria-label={`Remove ${resolvePortfolioName(portfolioId, options)}`}
                onClick={() =>
                  onChange(togglePortfolioId(selectedPortfolioIds, portfolioId, false))
                }
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          <button
            type="button"
            className="text-xs font-medium text-foreground-muted hover:text-foreground"
            onClick={() => onChange([])}
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  );
}
